/**
 * OptimizerModal presentational controls.
 *
 * Three stateless inputs the optimizer's strategy config is built from: a segmented
 * number picker, a toggle row, and a 0-to-max select. No hooks and no data access, they
 * render from props alone, which is why they lift out of OptimizerModal.tsx (873 lines
 * against the 800 limit) as a straight move.
 */

export function ConfigControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: number;
  options: number[];
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-theme-text-secondary block mb-1.5">{label}</label>
      <div className="flex gap-1">
        {options.map((n) => (
          <button
            type="button"
            key={n}
            onClick={() => onChange(n)}
            className={`flex-1 h-8 text-xs rounded-lg font-medium transition ${
              value === n
                ? 'bg-purple-600 text-white'
                : 'bg-theme-surface text-theme-text-secondary hover:text-theme-text hover:bg-theme-surface-hover'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export function FilterToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <span className="text-sm text-theme-text-secondary">{label}</span>
        <p className="text-xs text-theme-text-tertiary">{description}</p>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`mt-0.5 w-9 h-5 rounded-full transition-colors relative shrink-0 ${
          checked ? 'bg-purple-500' : 'bg-theme-border'
        }`}
      >
        <div
          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
            checked ? 'left-4' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  );
}

export function BoostControl({
  label,
  value,
  max,
  onChange,
}: {
  label: string;
  value: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-theme-text-secondary">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="bg-theme-surface text-theme-text text-sm rounded px-2 py-1 border border-theme-border focus:border-purple-500 focus:outline-none"
      >
        {Array.from({ length: max + 1 }, (_, i) => i).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </div>
  );
}
